import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    Image,
    StyleSheet,
    ActivityIndicator,
    Alert,
    Animated,
    Platform,
    Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import { getBusinessDetails, getMySales } from '../../api/marketplace';
import { getImageUrl } from '../../utils/imageUtils';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const MyBusinessesScreen = ({ navigation }) => {
    const { user } = useAuth();
    const insets = useSafeAreaInsets();
    const scrollY = useRef(new Animated.Value(0)).current;

    const [myBusinesses, setMyBusinesses] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchMyBusinesses();
    }, []);

    const fetchMyBusinesses = async () => {
        try {
            setLoading(true);
            const userId = user?.id;
            if (!userId) return;

            // Fetch business details and sales separately for resilience
            const results = await Promise.allSettled([
                getBusinessDetails(userId),
                getMySales()
            ]);

            if (results[0].status === 'fulfilled' && results[0].value) {
                setMyBusinesses([results[0].value]);
            } else {
                console.log('Business fetch failed or none found:', results[0].reason);
                setMyBusinesses([]);
            }

            if (results[1].status === 'fulfilled') {
                setSales(results[1].value || []);
            } else {
                console.log('Sales fetch failed:', results[1].reason);
                setSales([]);
            }
        } catch (error) {
            console.error('Data fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleEdit = (business) => {
        navigation.navigate('CreateBusiness', { business, isEditing: true });
    };

    // Header Animation values
    const headerHeight = 220;

    const headerOpacity = scrollY.interpolate({
        inputRange: [0, 80],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const stickyHeaderOpacity = scrollY.interpolate({
        inputRange: [80, 120],
        outputRange: [0, 1],
        extrapolate: 'clamp',
    });

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#6366F1" />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar style="light" />

            {/* Background Aura Section (Fixed) */}
            <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
                <LinearGradient
                    colors={['#8B5CF6', '#7C3AED', '#6D28D9']}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.auraContainer}>
                    <View style={[styles.aura, styles.aura1]} />
                    <View style={[styles.aura, styles.aura2]} />
                    <View style={[styles.aura, styles.aura3]} />
                </View>
            </View>

            {/* Mini Sticky Header */}
            <Animated.View
                style={[
                    styles.miniStickyHeader,
                    { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
                ]}
                pointerEvents="box-none"
            >
                <LinearGradient
                    colors={['#8B5CF6', '#7C3AED']}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                />
                <View style={styles.miniHeaderContent}>
                    <H2 style={styles.miniHeaderTitle}>My Businesses</H2>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
                        <Ionicons name="chevron-back" size={20} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </Animated.View>

            <Animated.ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollViewContent}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
            >
                {/* Full Hero Header */}
                <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 20, opacity: headerOpacity }]}>
                    <View style={styles.topBar}>
                        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
                            <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.heroTitleBlock}>
                        <Caption style={styles.heroSubtitle}>MANAGE • GROW</Caption>
                        <H2 style={styles.heroTitle}>Your Storefront</H2>
                    </View>
                </Animated.View>

                {/* Content Surface */}
                <View style={styles.contentSurface}>
                    {myBusinesses.length === 0 ? (
                        <View style={styles.emptyState}>
                            <View style={styles.emptyIconContainer}>
                                <Ionicons name="storefront-outline" size={64} color="#D1D5DB" />
                            </View>
                            <H2 style={styles.emptyTitle}>No Businesses Listed</H2>
                            <Body2 style={styles.emptyText}>You haven't listed any businesses yet. Start reaching thousands of youth today!</Body2>
                            <TouchableOpacity
                                style={styles.createButton}
                                onPress={() => navigation.navigate('CreateBusiness')}
                            >
                                <ButtonText style={styles.createButtonText}>LIST YOUR FIRST BUSINESS</ButtonText>
                            </TouchableOpacity>
                        </View>
                    ) : (
                        myBusinesses.map((business) => (
                            <View key={business.id} style={styles.businessCard}>
                                <View style={styles.cardInfo}>
                                    <Image source={{ uri: getImageUrl(business.logo_url) }} style={styles.logo} />
                                    <View style={styles.textDetails}>
                                        <H2 style={styles.businessName}>{business.business_name}</H2>
                                        <Caption style={styles.category}>{business.category?.name || 'General'}</Caption>
                                        <View style={styles.statusBadge}>
                                            <View style={styles.statusDot} />
                                            <Caption style={styles.statusText}>ACTIVE</Caption>
                                        </View>
                                    </View>
                                </View>

                                <View style={styles.statsRow}>
                                    <View style={styles.statItem}>
                                        <H3 style={styles.statValue}>{business.total_reviews || 0}</H3>
                                        <Caption style={styles.statLabel}>Reviews</Caption>
                                    </View>
                                    <View style={[styles.statItem, styles.statDivider]}>
                                        <H3 style={styles.statValue}>{business.rating || '5.0'}</H3>
                                        <Caption style={styles.statLabel}>Rating</Caption>
                                    </View>
                                    <View style={styles.statItem}>
                                        <H3 style={styles.statValue}>-</H3>
                                        <Caption style={styles.statLabel}>Views</Caption>
                                    </View>
                                </View>

                                <View style={styles.cardActions}>
                                    <TouchableOpacity
                                        style={styles.editButton}
                                        onPress={() => handleEdit(business)}
                                    >
                                        <Ionicons name="create-outline" size={18} color="#6366F1" />
                                        <Caption style={styles.editButtonText}>EDIT BUSINESS</Caption>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={styles.viewButton}
                                        onPress={() => navigation.navigate('BusinessDetail', { listingId: business.id })}
                                    >
                                        <Ionicons name="eye-outline" size={18} color="#6B7280" />
                                        <Caption style={styles.viewButtonText}>PREVIEW</Caption>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        ))
                    )}

                    {/* Sales Section */}
                    {sales.length > 0 && (
                        <View style={styles.salesSection}>
                            <View style={styles.sectionHeader}>
                                <H2 style={styles.surfaceTitle}>Recent Sales</H2>
                                <TouchableOpacity
                                    onPress={() => navigation.navigate('VendorOrders')}
                                    style={styles.pillLink}
                                >
                                    <Text style={styles.pillLinkText}>MY ORDERS</Text>
                                    <Ionicons name="chevron-forward" size={12} color="#6366F1" />
                                </TouchableOpacity>
                            </View>

                            {sales.slice(0, 3).map((sale) => (
                                <TouchableOpacity
                                    key={sale.id}
                                    style={styles.saleItem}
                                    onPress={() => navigation.navigate('VendorOrderDetail', { orderId: sale.order_id })}
                                >
                                    <View style={styles.saleHeader}>
                                        <Body1 style={styles.saleItemName} numberOfLines={1}>{sale.item?.name}</Body1>
                                        <H3 style={styles.saleAmount}>KES {Number(sale.price_at_purchase * sale.quantity).toLocaleString()}</H3>
                                    </View>
                                    <View style={styles.saleFooter}>
                                        <View style={styles.buyerInfo}>
                                            <Ionicons name="person-circle-outline" size={14} color="#9CA3AF" />
                                            <Caption style={styles.buyerName}>{sale.order?.buyer?.name || 'Anonymous'}</Caption>
                                        </View>
                                        <View style={[
                                            styles.saleStatus,
                                            { backgroundColor: sale.order?.status === 'completed' ? '#ECFDF5' : sale.order?.status === 'paid_processing' ? '#EEF2FF' : '#FEF3C7' }
                                        ]}>
                                            <Caption style={[
                                                styles.saleStatusText,
                                                { color: sale.order?.status === 'completed' ? '#059669' : sale.order?.status === 'paid_processing' ? '#6366F1' : '#D97706' }
                                            ]}>
                                                {sale.order?.status === 'completed' ? 'COMPLETED' : sale.order?.status === 'paid_processing' ? 'READY' : sale.order?.status.toUpperCase()}
                                            </Caption>
                                        </View>
                                    </View>
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}

                    {/* Customer Feedback Section */}
                    {sales.some(s => s.order?.buyer_feedback) && (
                        <View style={styles.feedbackSection}>
                            <H2 style={styles.surfaceTitle}>Customer Reviews</H2>
                            {Array.from(new Set(sales.filter(s => s.order?.buyer_feedback).map(s => s.order_id)))
                                .slice(0, 3)
                                .map(orderId => {
                                    const sampleSale = sales.find(s => s.order_id === orderId);
                                    const order = sampleSale.order;
                                    return (
                                        <View key={orderId} style={styles.feedbackCard}>
                                            <View style={styles.feedbackHeader}>
                                                <Caption style={styles.feedbackBuyer}>{order.buyer?.name || 'Anonymous'}</Caption>
                                                <View style={styles.starsRow}>
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <Ionicons
                                                            key={star}
                                                            name="star"
                                                            size={10}
                                                            color={star <= (order.satisfaction_rating || 5) ? "#F59E0B" : "#E5E7EB"}
                                                        />
                                                    ))}
                                                </View>
                                            </View>
                                            <Body2 style={styles.feedbackText}>"{order.buyer_feedback}"</Body2>
                                            <Caption style={styles.feedbackDate}>{new Date(order.updatedAt).toLocaleDateString()}</Caption>
                                        </View>
                                    );
                                })}
                        </View>
                    )}

                    <View style={styles.footerSection}>
                        <Caption style={styles.footerText}>Business Insights • Youth Connect</Caption>
                    </View>
                </View>
            </Animated.ScrollView>

            {myBusinesses.length > 0 && (
                <TouchableOpacity
                    style={styles.fab}
                    onPress={() => navigation.navigate('CreateBusiness')}
                >
                    <Ionicons name="add" size={30} color="#FFFFFF" />
                </TouchableOpacity>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F5F3FF', // Soft Purple tint
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F5F3FF',
    },
    headerBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 0,
    },
    auraContainer: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    aura: {
        position: 'absolute',
        borderRadius: 150,
        opacity: 0.15,
    },
    aura1: {
        width: 250,
        height: 250,
        top: -50,
        right: -30,
        backgroundColor: '#C084FC',
    },
    aura2: {
        width: 200,
        height: 200,
        top: 50,
        left: -50,
        backgroundColor: '#A855F7',
    },
    aura3: {
        width: 180,
        height: 180,
        bottom: -60,
        right: 40,
        backgroundColor: '#581C87',
    },
    miniStickyHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 90,
        elevation: 4,
    },
    miniHeaderContent: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: 44,
        marginTop: 20,
    },
    miniHeaderTitle: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
    miniBackBtn: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollView: {
        flex: 1,
        zIndex: 1,
    },
    scrollViewContent: {
        paddingBottom: 0,
    },
    heroHeader: {
        paddingHorizontal: 20,
        paddingBottom: 30,
        zIndex: 2,
    },
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 15,
    },
    backCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.25)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitleBlock: {
        marginBottom: 5,
    },
    heroSubtitle: {
        color: 'rgba(255,255,255,0.8)',
        letterSpacing: 1.5,
        fontWeight: '800',
        fontSize: 10,
    },
    heroTitle: {
        color: '#FFFFFF',
        fontSize: 24,
        fontWeight: '800',
        marginTop: 2,
    },
    contentSurface: {
        backgroundColor: '#F5F3FF', // Soft Purple tint
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        paddingTop: 24,
        paddingHorizontal: 18,
        minHeight: SCREEN_HEIGHT - 100,
        zIndex: 5,
        marginTop: -15,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 40,
    },
    emptyIconContainer: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: '#EDE9FE',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    emptyText: {
        color: '#6B7280',
        textAlign: 'center',
        paddingHorizontal: 20,
        lineHeight: 20,
        fontSize: 13,
        marginBottom: 25,
    },
    createButton: {
        backgroundColor: '#7C3AED',
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 14,
        ...getShadow('md'),
    },
    createButtonText: {
        color: '#FFFFFF',
        fontWeight: '900',
        fontSize: 13,
    },
    businessCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 16,
        marginBottom: 15,
        borderWidth: 1,
        borderColor: '#EDE9FE',
        ...getShadow('xs'),
    },
    cardInfo: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    logo: {
        width: 54,
        height: 54,
        borderRadius: 14,
        backgroundColor: '#F3F4F6',
    },
    textDetails: {
        flex: 1,
        marginLeft: 14,
    },
    businessName: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 1,
    },
    category: {
        color: '#6B7280',
        fontSize: 11,
        marginBottom: 4,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#ECFDF5',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 6,
        alignSelf: 'flex-start',
    },
    statusDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#10B981',
        marginRight: 4,
    },
    statusText: {
        fontSize: 8,
        fontWeight: '900',
        color: '#047857',
    },
    statsRow: {
        flexDirection: 'row',
        paddingVertical: 12,
        marginTop: 12,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#F3F4F6',
    },
    statItem: {
        flex: 1,
        alignItems: 'center',
    },
    statDivider: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderColor: '#F3F4F6',
    },
    statValue: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111827',
    },
    statLabel: {
        fontSize: 10,
        marginTop: 1,
    },
    cardActions: {
        flexDirection: 'row',
        marginTop: 12,
        gap: 10,
    },
    editButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F3FF',
        paddingVertical: 10,
        borderRadius: 12,
        gap: 6,
    },
    editButtonText: {
        color: '#7C3AED',
        fontWeight: '900',
        fontSize: 11,
    },
    viewButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FAFB',
        paddingVertical: 10,
        borderRadius: 12,
        gap: 6,
    },
    viewButtonText: {
        color: '#6B7280',
        fontWeight: '900',
        fontSize: 11,
    },
    footerSection: {
        alignItems: 'center',
        paddingVertical: 30,
    },
    footerText: {
        color: '#9CA3AF',
        fontSize: 9,
    },
    fab: {
        position: 'absolute',
        bottom: 25,
        right: 20,
        width: 54,
        height: 54,
        borderRadius: 27,
        backgroundColor: '#7C3AED',
        alignItems: 'center',
        justifyContent: 'center',
        ...getShadow('lg'),
        zIndex: 10,
    },
    salesSection: {
        marginTop: 15,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    pillLink: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
        backgroundColor: '#EDE9FE',
        borderRadius: 16,
        gap: 3,
    },
    pillLinkText: {
        fontSize: 9,
        fontWeight: '900',
        color: '#7C3AED',
        letterSpacing: 0.5,
    },
    saleItem: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 14,
        marginBottom: 10,
        ...getShadow('xs'),
    },
    saleHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    saleItemName: {
        flex: 1,
        marginRight: 8,
        fontSize: 13,
    },
    saleAmount: {
        color: '#111827',
        fontSize: 14,
    },
    saleFooter: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    buyerInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    buyerName: {
        color: '#9CA3AF',
        fontSize: 11,
    },
    saleStatus: {
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 6,
    },
    saleStatusText: {
        fontSize: 9,
        fontWeight: '900',
    },
    feedbackSection: {
        marginTop: 20,
    },
    surfaceTitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#4C1D95', // Deep purple
        marginBottom: 15,
    },
    feedbackCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 14,
        marginTop: 10,
        borderWidth: 1,
        borderColor: '#EDE9FE',
    },
    feedbackHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    feedbackBuyer: {
        color: '#111827',
        fontWeight: '700',
        fontSize: 12,
    },
    starsRow: {
        flexDirection: 'row',
        gap: 2,
    },
    feedbackText: {
        color: '#4B5563',
        fontStyle: 'italic',
        lineHeight: 16,
        fontSize: 12,
    },
    feedbackDate: {
        marginTop: 6,
        color: '#9CA3AF',
        fontSize: 9,
    },
});

export default MyBusinessesScreen;
